import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, Facebook, Twitter, Instagram, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator
} from './ui/dropdown-menu';

const Header = ({ logo, onDonateClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { label: 'About', id: 'about' },
    { label: 'Priorities', id: 'priorities' },
    { label: 'Get Involved', id: 'get-involved' }
  ];

  const dropdownItems = [
    { label: 'About', id: 'about' },
    { label: 'Priorities', id: 'priorities' },
    { label: 'Videos', id: 'videos' },
    { label: 'Get Involved', id: 'get-involved' }
  ];

  // Social media links
  const socialLinks = {
    facebook: "https://www.facebook.com/share/1Bs7mw2ynd/?mibextid=wwXIfr",
    instagram: "https://www.instagram.com/cliandaflorencefornyassembly?igsh=MTRkdml0b3J4bjN5bg%3D%3D&utm_source=qr",
    twitter: "#"
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-purple-900 shadow-lg py-3' : 'bg-purple-900/80 backdrop-blur-md py-4'
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo dropdown in corner */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex-shrink-0 flex items-center gap-2 rounded-lg p-1 transition-transform duration-300 hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                aria-label="Open site menu"
                data-testid="logo-dropdown-trigger"
              >
                <img
                  src={logo}
                  alt="Clianda Florence Campaign Logo"
                  className={`w-auto transition-all duration-300 ${
                    isScrolled ? 'h-12 md:h-14' : 'h-14 md:h-16'
                  }`}
                />
                <ChevronDown className="h-5 w-5 text-white" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 bg-purple-950 text-white border-white/20" data-testid="logo-dropdown-menu">
              <DropdownMenuItem onSelect={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="focus:bg-white/10 focus:text-white cursor-pointer" data-testid="logo-menu-home">
                Home
              </DropdownMenuItem>
              {dropdownItems.map((item) => (
                <DropdownMenuItem key={item.id} onSelect={() => scrollToSection(item.id)} className="focus:bg-white/10 focus:text-white cursor-pointer" data-testid={`logo-menu-${item.id}`}>
                  {item.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem onSelect={onDonateClick} className="focus:bg-white/10 focus:text-white cursor-pointer font-bold" data-testid="logo-menu-donate">
                Donate
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/20" />
              <DropdownMenuItem asChild className="focus:bg-white/10 focus:text-white cursor-pointer text-sm">
                <a href="/privacy-policy/" data-testid="logo-menu-privacy">Privacy Policy</a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="focus:bg-white/10 focus:text-white cursor-pointer text-sm">
                <a href="/terms-and-conditions/" data-testid="logo-menu-terms">Terms &amp; Conditions</a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Desktop Menu - Right Side */}
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-white hover:text-purple-200 font-semibold text-base transition-colors uppercase tracking-wide"
              >
                {item.label}
              </button>
            ))}
            
            {/* Social Icons */}
            <div className="flex items-center gap-3 ml-4 pl-4 border-l border-white/20">
              <a 
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all"
                aria-label="Facebook"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a 
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4" />
              </a>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-white"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>

        {/* Mobile Dropdown Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 bg-purple-950/95 rounded-lg -mx-6 px-6">
            <nav className="flex flex-col gap-4 pt-4">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left text-white hover:text-purple-200 font-semibold py-2 uppercase tracking-wide transition-colors"
                >
                  {item.label}
                </button>
              ))}
              
              {/* Mobile Social Links */}
              <div className="flex items-center gap-4 pt-4 border-t border-white/20">
                <a 
                  href={socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white"
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
                <a 
                  href={socialLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white"
                  aria-label="Twitter"
                >
                  <Twitter className="h-5 w-5" />
                </a>
                <a 
                  href={socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white"
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export { Header };
